/**
 * Two-column queue table.
 *
 *   Verbatim (CSS-truncated to fit the column)
 *     |  Status badge
 *
 * Width-based truncation: `table-fixed` on the `<Table>` makes columns
 * respect their declared widths (Status is fixed; Verbatim takes the
 * remainder), and `truncate` on the verbatim cell clips the text with
 * an ellipsis at the actual rendered column edge. Avoids the brittle
 * "guess a char count" approach — the visible length now adapts to
 * viewport width automatically. Full text on hover via `title=` for
 * the annotator to read mid-scan without clicking through.
 *
 * The record id used to be its own column but was dropped — it carried
 * no information the annotator needed. Full id is still surfaced on row
 * hover via the row's `title=` for debugging.
 *
 * Default row click navigates to the annotation flow at
 *   `/v4/go-lab/annotate/queue/:recordId`
 * The review queue reuses this table with an `onRowClick` handler
 * that branches by status — disputed → adjudication flow at
 *   `/v4/go-lab/annotate/review/:recordId`
 * agreed → opens `FinaliseAgreedDialog` in place (no navigation).
 *
 * Empty body shows the queue copy in a single colspan row; the per-page
 * wording is overridable via `emptyStateCopy`.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '../../../../../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../ui/table';
import type { DatasetRecord } from '../../types';
import { statusBadge } from '../statusBadge';

interface Props {
  records: DatasetRecord[];
  /**
   * Row-click handler. Defaults to navigating to the personal annotation
   * flow at `/v4/go-lab/annotate/queue/{record.id}`. The review queue
   * overrides this to branch by status — disputed → adjudication flow;
   * agreed → finalise modal.
   */
  onRowClick?: (record: DatasetRecord) => void;
  /** Body copy for the empty state. Defaults to the personal-queue line. */
  emptyStateCopy?: string;
}


const DEFAULT_EMPTY_COPY =
  'No records in your queue. Switch user to see different assignments.';

const QueueTable: React.FC<Props> = ({
  records,
  onRowClick,
  emptyStateCopy = DEFAULT_EMPTY_COPY,
}) => {
  const navigate = useNavigate();
  const handleClick = (record: DatasetRecord) => {
    if (onRowClick) {
      onRowClick(record);
      return;
    }
    navigate(`/v4/go-lab/annotate/queue/${record.id}`);
  };
  const isEmpty = records.length === 0;

  return (
    <Table className="table-fixed" data-testid="queue-table">
      <TableHeader>
        <TableRow className="border-border">
          <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Verbatim
          </TableHead>
          <TableHead className="w-[140px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Status
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isEmpty ? (
          <TableRow>
            <TableCell
              colSpan={2}
              className="py-12 text-center text-sm text-muted-foreground"
              data-testid="queue-empty-state"
            >
              {emptyStateCopy}
            </TableCell>
          </TableRow>
        ) : (
          records.map((record) => {
            const badge = statusBadge(record.status);
            return (
              <TableRow
                key={record.id}
                onClick={() => handleClick(record)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick(record);
                  }
                }}
                role="button"
                tabIndex={0}
                className="cursor-pointer border-border hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`queue-row-${record.id}`}
                title={record.id}
              >
                <TableCell
                  className="truncate text-[13px] text-foreground"
                  title={record.verbatim}
                >
                  {record.verbatim}
                </TableCell>
                <TableCell>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
};

export default QueueTable;
